interface ValidationResult {
  ticketId?: string | null;
  ticketNumber?: string | number | null;
  errors?: string[];
}

interface CandidateLine {
  ticketId?: string | null;
  ticketNumber?: string | number | null;
  description?: string | null;
  amount?: number | null;
}

interface PushErrorDetails {
  validationResults?: ValidationResult[];
  groupValidation?: { errors?: string[] };
  qboFault?: { message?: string; detail?: string; code?: string; element?: string | null };
  candidateLines?: CandidateLine[];
  rawFault?: string;
  ticketIds?: string[];
}

function unique(messages: string[]): string[] {
  return [...new Set(messages.map((message) => message.trim()).filter(Boolean))];
}

export function isQboPushInProgressError(err: unknown): boolean {
  const apiErr = err as {
    code?: unknown;
    data?: { code?: unknown; error?: unknown } | null;
  } | null;
  return apiErr?.code === 'QBO_PUSH_IN_PROGRESS' ||
    apiErr?.data?.code === 'QBO_PUSH_IN_PROGRESS' ||
    apiErr?.data?.error === 'QBO_PUSH_IN_PROGRESS';
}

export function extractQboPushTicketIds(err: unknown): string[] {
  const apiErr = err as {
    data?: { details?: { ticketIds?: unknown } } | null;
  } | null;
  const ticketIds = apiErr?.data?.details?.ticketIds;
  if (!Array.isArray(ticketIds)) return [];
  return [...new Set(ticketIds.filter((id): id is string => typeof id === 'string' && id.length > 0))];
}

// Backend ApiErrors are plain objects. QBO/grouped-push failures serialize
// their actionable data under data.details, so identify the ticket and QBO line
// instead of showing raw JSON (#677).
export function extractTransitionErrorMessage(err: unknown): string {
  const apiErr = err as {
    message?: string;
    data?: { error?: string; message?: string; details?: PushErrorDetails } | null;
  } | undefined;
  const details = apiErr?.data?.details;
  const messages: string[] = [];

  for (const result of details?.validationResults || []) {
    const ticket = result.ticketNumber || result.ticketId || 'unknown';
    for (const error of result.errors || []) messages.push(`Ticket #${ticket}: ${error}`);
  }
  for (const error of details?.groupValidation?.errors || []) messages.push(`Group: ${error}`);

  if (details?.qboFault) {
    const fault = details.qboFault;
    const faultMessage = [fault.message, fault.detail].filter(Boolean).join(' — ');
    messages.push(`QuickBooks rejected the document${faultMessage ? `: ${faultMessage}` : ''}`);
    for (const line of details.candidateLines || []) {
      const ticket = line.ticketNumber || line.ticketId || 'unknown';
      const pricing = line.amount != null ? ` ($${Number(line.amount).toFixed(2)})` : '';
      messages.push(`Possible line — ticket #${ticket}: ${line.description || 'QBO line'}${pricing}`);
    }
    if (!(details.candidateLines || []).length && details.rawFault) messages.push(details.rawFault);
  }

  const structured = unique(messages);
  if (structured.length > 0) return structured.join('\n');
  return apiErr?.data?.message || apiErr?.message || apiErr?.data?.error ||
    (err instanceof Error ? err.message : '') || 'Action failed';
}

interface BaserowRowResult {
  ticketId?: string;
  action?: string;
  reason?: string;
  error?: string;
}

interface QboPushResult {
  docType?: 'estimate' | 'invoice';
  doc?: { number?: string | null };
  baserowSync?: {
    skipped?: boolean;
    reason?: string;
    error?: string;
    rows?: BaserowRowResult[];
  };
}

const BASEROW_SKIP_REASONS: Record<string, string> = {
  flag_off: 'Ayrow sync is turned off in Admin settings',
  missing_trello_short_link: 'the site has no Trello card link',
};

function humanizeBaserowReason(reason: string | undefined, fallback: string): string {
  if (!reason) return fallback;
  return BASEROW_SKIP_REASONS[reason] || reason;
}

export function extractBaserowSyncWarning(result: QboPushResult | null | undefined): string | null {
  const sync = result?.baserowSync;
  if (!sync) return null;
  const problems = unique([
    ...(sync.skipped ? [humanizeBaserowReason(sync.reason, 'sync skipped')] : []),
    ...(sync.error ? [sync.error] : []),
    ...(sync.rows || [])
      .filter((row) => row.action === 'skipped' || row.action === 'error')
      .map((row) => humanizeBaserowReason(row.reason, row.error || `${row.action} for ticket ${row.ticketId || 'unknown'}`)),
  ]);
  if (problems.length === 0) return null;

  const kind = result?.docType === 'invoice' ? 'Invoice' : 'Estimate';
  const number = result?.doc?.number ? ` #${result.doc.number}` : '';
  return `${kind}${number} created — Ayrow row not created/updated: ${problems.join('; ')}`;
}
